import type { Ratecard, RatecardRole, ResourceLine } from "./types";

/**
 * Поиск ставки по названию роли — тот же смысл, что у
 * `VLOOKUP(D; Рейткарта!A:D; 4; FALSE)` в шаблонах Braining.
 *
 * Ключ — человеческое название роли, а не идентификатор: так устроены исходные
 * таблицы, и переименование роли в рейткарте так же осмысленно ломает связь,
 * как `#N/A` в таблице. Отличие в том, что мы это показываем, а не роняем расчёт.
 */
export function buildRateIndex(ratecard: Ratecard): Map<string, RatecardRole> {
  const index = new Map<string, RatecardRole>();
  for (const group of ratecard.groups) {
    for (const role of group.roles) {
      index.set(normalize(role.name), role);
    }
  }
  return index;
}

function normalize(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

export function findRole(index: Map<string, RatecardRole>, name: string): RatecardRole | undefined {
  return index.get(normalize(name));
}

/**
 * Цена за единицу для строки.
 *
 * Приоритет: явно заданная цена → ставка из рейткарты. Явная цена важна не
 * только для строк с прямой ценой: она замораживает ставку в утверждённой
 * смете, чтобы правка рейткарты не пересчитывала прошлые расчёты задним числом.
 */
export function resolveRate(
  line: ResourceLine,
  index: Map<string, RatecardRole>,
): { rate: number; missing: boolean } {
  if (typeof line.unitPrice === "number") {
    return { rate: line.unitPrice, missing: false };
  }
  /*
   * Строка со своей ценой берёт цену только из себя.
   *
   * У неё `priceSource: "direct"` — закупка, услуга, пакет; цены нет —
   * значит её ещё не назвали. Пока она искала себя в прайсе по имени,
   * «Дизайнер» в списке закупок молча считался по часовой ставке, и
   * `rateMissing` не загорался: строка без цены выглядела посчитанной.
   */
  if (line.priceSource === "direct") return { rate: 0, missing: true };
  const role = findRole(index, line.name);
  if (!role) return { rate: 0, missing: true };
  return { rate: role.rate, missing: false };
}

/** Все роли рейткарты одним списком — для выпадашек в интерфейсе. */
export function flatRoles(ratecard: Ratecard): RatecardRole[] {
  return ratecard.groups.flatMap((g) => g.roles);
}
